import { usePageMeta } from '../lib/meta'

// The page registries (server.json, glama.json), the MCP README and the server's 401 hint point at
// freightratesmcp.com/mcp. It is the one place a customer learns how to connect an AI assistant.

const HOSTED = 'https://mcp.freightratesmcp.com/mcp'

const TOOLS: Array<[string, string]> = [
  ['get_freight_indices', 'The weekly rate index, one row per trade bucket.'],
  ['get_lane_rate', 'The latest indicative rate for one port-pair.'],
  ['get_lane_offers', 'Every sailing card behind a lane: carrier, vessel, voyage, ETD, prices.'],
  ['get_lane_history', 'The daily median rate curve for a lane.'],
  [
    'get_lane_spot_rates',
    "Marketplace spot rates for a lane, refreshed daily: FCL 20', 40' and 40' HC side by side, and an LCL ladder by shipment size. Say what your cargo is and it picks the size. Each offer says what its price covers.",
  ],
  [
    'get_spot_quote',
    'A live search of any lane and mode across 55 sources. Slow ones answer late, so repeat the request until it is complete.',
  ],
  ['list_rate_providers', 'The sources behind the live search and what each can do.'],
  ['get_booking_contacts', 'The published vendor contacts for a quote. Does not place a booking.'],
]

const ASKS = [
  "What is the cheapest 40' HC from Shanghai to Manzanillo this week, and who quotes it?",
  'Show me the rate trend Ningbo to Manzanillo over the last two months.',
  'I have 2.5 m3 and 800 kg to ship Shanghai to Manzanillo as LCL. What will it cost?',
  'Compare the sources for Yantian to Manzanillo and tell me which prices include destination charges.',
]

const CURSOR = `{
  "mcpServers": {
    "freight-rates": {
      "url": "${HOSTED}",
      "headers": { "Authorization": "Bearer YOUR_API_KEY" }
    }
  }
}`

const CLAUDE = `{
  "mcpServers": {
    "freight-rates": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "${HOSTED}",
               "--header", "Authorization: Bearer YOUR_API_KEY"]
    }
  }
}`

function Code({ children }: { children: string }) {
  return (
    <pre className="mt-3 overflow-x-auto rounded-lg border border-border-strong bg-surface-1 p-4 text-xs leading-relaxed">
      <code>{children}</code>
    </pre>
  )
}

export function McpPage() {
  usePageMeta(
    'Use FreightRatesMCP from Claude, ChatGPT and Cursor',
    'Connect your AI assistant to ocean freight rates: the weekly index, lane rates, sailings, history, and FCL and LCL spot rates with what each price covers.',
    '/mcp',
    ['en']
  )
  return (
    <div className="mx-auto max-w-3xl px-5 pt-14 pb-16">
      <h1 className="font-display text-4xl font-light">
        Use FreightRatesMCP from your AI assistant
      </h1>
      <p className="mt-3 text-muted leading-relaxed">
        Ask Claude, ChatGPT or Cursor about ocean freight rates and get answers from the same data
        as this site: the weekly index, lane rates, every sailing behind them, history, and spot
        rates for containers (FCL) and part loads (LCL). Ports are UN/LOCODEs, for example CNSHA
        Shanghai, CNYTN Yantian (Shenzhen) and MXZLO Manzanillo.
      </p>

      <h2 className="mt-10 font-display text-2xl">What you can ask</h2>
      <ul className="mt-3 list-disc space-y-2 pl-5 text-muted">
        {ASKS.map((a) => (
          <li key={a}>{a}</li>
        ))}
      </ul>

      <h2 className="mt-10 font-display text-2xl">Tools</h2>
      <div className="mt-3 overflow-hidden rounded-lg border border-border-strong">
        <table className="w-full text-left text-sm">
          <tbody>
            {TOOLS.map(([name, what]) => (
              <tr key={name} className="border-b border-border-strong last:border-0">
                <td className="whitespace-nowrap px-4 py-3 align-top font-mono text-xs">{name}</td>
                <td className="px-4 py-3 text-muted">{what}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-sm text-muted">
        Prices are USD and indicative, not firm quotes. Sources do not all quote the same basis, so
        each spot offer says whether origin and destination charges are in the price.
      </p>

      <h2 className="mt-10 font-display text-2xl">Set up</h2>
      <h3 className="mt-5 font-semibold">Cursor, Windsurf, VS Code</h3>
      <Code>{CURSOR}</Code>
      <h3 className="mt-6 font-semibold">Claude Desktop</h3>
      <Code>{CLAUDE}</Code>
      <h3 className="mt-6 font-semibold">ChatGPT</h3>
      <p className="mt-2 text-muted">
        Add a custom connector with the URL <code className="text-xs">{HOSTED}</code> and your API
        key as the Bearer token.
      </p>

      <h2 className="mt-10 font-display text-2xl">Get an API key</h2>
      <p className="mt-3 text-muted leading-relaxed">
        The API and the MCP server are on the Pro plan. Open <b>Settings, API keys</b> in your
        Navo24 workspace, or your{' '}
        <a className="text-signal underline" href="/account">
          account
        </a>
        , and copy a <code className="text-xs">tmcp_</code> key. It must belong to an organisation
        with an active FreightRates product. Browsing the same rates on this site is free.
      </p>
    </div>
  )
}
